import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';

const serviceLinks = [
  { path: '/title-services', label: 'Title Services', icon: '🏠' },
  { path: '/title-commitment-services', label: 'Title Commitment Services', icon: '📋' },
  { path: '/settlement-services', label: 'Settlement Services', icon: '🤝' },
  { path: '/curative-services', label: 'Curative Services', icon: '' },
  { path: '/tax-services', label: 'Tax Services', icon: '💼' },
];

const navLinks = [
  { path: '/', label: 'Home' },
  { path: '/about', label: 'About' },
  { path: '/services', label: 'Services', hasDropdown: true },
  { path: '/contact', label: 'Contact' },
];

const Navigation = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;
  const isServiceActive = serviceLinks.some((s) => location.pathname === s.path);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
    setMobileServicesOpen(false);
    setDropdownOpen(false);
  }, [location.pathname]);

  return (
    <nav className="bg-primary sticky top-0 z-50 shadow-lg border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">

          {/* Logo */}
          <Link to="/" className="flex-shrink-0 flex items-center">
            <img
              src="./assets/Images/Logo/logo.png"
              alt="Logo"
              className="w-[180px] h-auto"
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center space-x-1">
            {navLinks.map((link) =>
              link.hasDropdown ? (
                <div key={link.path} className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setDropdownOpen((prev) => !prev)}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 focus:outline-none ${isActive(link.path) || isServiceActive
                      ? 'text-white bg-white/20'
                      : 'text-gray-300 hover:text-white hover:bg-white/10'
                      }`}
                  >
                    {link.label}
                    <ChevronDown
                      className={`h-4 w-4 opacity-70 transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''
                        }`}
                    />
                  </button>

                  {/* Dropdown Panel */}
                  {dropdownOpen && (
                    <div className="absolute left-0 top-full mt-2 w-60 rounded-2xl border border-white/10 bg-zinc-100 shadow-2xl overflow-hidden">
                      <div className="py-2">
                        {serviceLinks.map((item) => (
                          <Link
                            key={item.path}
                            to={item.path}
                            className={`flex items-center gap-3 px-4 py-3 text-sm transition-all duration-150 ${isActive(item.path)
                              ? 'bg-white/10 text-gray-900 font-bold'
                              : 'text-gray-800 hover:bg-white/10 '
                              }`}
                          >
                            {/* <span className="text-base">{item.icon}</span> */}
                            {item.label}
                          </Link>
                        ))}
                      </div>
                      <div className="px-4 py-3 border-t border-white/10">
                        <Link
                          to="/services"
                          className="block w-full text-center text-xs font-semibold uppercase tracking-widest text-gray-400 transition-colors"
                        >
                          View All Services →
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${isActive(link.path)
                    ? 'text-white bg-white/20'
                    : 'text-gray-300 hover:text-white hover:bg-white/10'
                    }`}
                >
                  {link.label}
                </Link>
              )
            )}

          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setMobileOpen((prev) => !prev)}
            className="md:hidden p-2 rounded-lg text-white hover:bg-white/10 focus:outline-none transition-colors"
            aria-label="Toggle menu"
            aria-expanded={mobileOpen}
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              {mobileOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden border-t border-white/10 bg-zinc-900/98 backdrop-blur-md">
          <div className="px-3 py-4 space-y-1">
            {navLinks.map((link) =>
              link.hasDropdown ? (
                <div key={link.path}>
                  <button
                    onClick={() => setMobileServicesOpen((prev) => !prev)}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-medium transition-all ${isServiceActive
                      ? 'text-white bg-white/20'
                      : 'text-gray-300 hover:text-white hover:bg-white/10'
                      }`}
                  >
                    {link.label}
                    <ChevronDown
                      className={`h-4 w-4 opacity-70 transition-transform duration-200 ${mobileServicesOpen ? 'rotate-180' : ''
                        }`}
                    />
                  </button>

                  {mobileServicesOpen && (
                    <div className="mt-1 ml-4 space-y-1 border-l-2 border-white/10 pl-4">
                      {serviceLinks.map((item) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${isActive(item.path)
                            ? 'text-white bg-white/15 font-medium'
                            : 'text-gray-400 hover:text-white hover:bg-white/10'
                            }`}
                        >
                          {/* <span>{item.icon}</span> */}
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`block px-4 py-3 rounded-xl text-sm font-medium transition-all ${isActive(link.path)
                    ? 'text-white bg-white/20'
                    : 'text-gray-300 hover:text-white hover:bg-white/10'
                    }`}
                >
                  {link.label}
                </Link>
              )
            )}

          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;